import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { Skeleton } from "@/components/ui/baseUi/skeleton" 
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { Button } from "@/components/ui/baseUi/button"
import { cn } from "@/modules/lib/utils"
import boxOfferPlanIcon from "@images/illustrations/boxOfferPlanIcon.svg"
import { BoxTerminatedIcon } from "@images/svgIcons/boxTerminatedIcon"
import { WarningIcon } from "@images/svgIcons/warningIcon"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { MemberDetailedEntity } from "@modules/members/entities/memberEntity"
import { MemberStatus } from "@modules/members/gateway/memberDto"
import { useMemberActions } from "@modules/members/hooks/useMember"
import { convertLasVisitedToNumberAndDays } from "@modules/members/memberUtils"
import {
  MemberSendOfferModal,
  type MemberSendOfferModalRef,
} from "@modules/members/sendOfferToMember/components/memberSendOfferModal"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HuCamembert } from "@modules/ui/components/huCambert"
import { HuNextBookingCard } from "@modules/ui/components/huEventMiniCard"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import { LoggerType } from "@modules/utils/loggerUtils"
import dayjs from "dayjs"
import { useRef } from "react"
import { FreeMode, Navigation, Pagination } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { z } from "zod"

type MemberTabDayToDayProps = {
  memberDetailed: MemberDetailedEntity
  className?: string
} & React.ComponentProps<"div">

export function MemberTabDayToDay({ memberDetailed, className, ...props }: MemberTabDayToDayProps) {
  const lastvisited = memberDetailed.lastVisited
    ? convertLasVisitedToNumberAndDays(memberDetailed.lastVisited)
    : { number: 0, daysSinceLastVisited: 0 }

  const { hideSidebar } = useSidebar()
  const canEditMember =
    useBoxStaffPermission(BoxStaffPermissions.EDIT_MEMBER) && memberDetailed.status !== MemberStatus.TERMINATED
  const canArchiveMember =
    useBoxStaffPermission(BoxStaffPermissions.ARCHIVE_MEMBER) && memberDetailed.status !== MemberStatus.TERMINATED
  const canSendOffer = useBoxStaffPermission(BoxStaffPermissions.SEND_OFFER)
  const confirmPopup = useConfirmPopup()
  const { archiveMember, updateMember } = useMemberActions(memberDetailed.id)

  const memberSendOfferModalRef = useRef<MemberSendOfferModalRef>(null)

  const memberDayToDaySchema = z.object({
    note: z.string().refine((value) => value !== (memberDetailed.note ?? ""), {
      message: word("box.member.table.dayToDay.noteError"),
    }),
  })

  const form = useAppForm({
    defaultValues: { note: memberDetailed.note ?? "" },
    validators: {
      onChange: memberDayToDaySchema,
    },
    onSubmit: async ({ value }) => {
      try {
        await updateMember(memberDetailed.setNote(value.note))
        toast.success(word("member.editMember.success"), {
          classNames: successToastClassName(),
        })
      } catch (e: unknown) {
        console.error(LoggerType.Service + "update member", e)
        toast.error(word("global.error.label"), {
          description: e instanceof Error ? e.message : String(e),
          classNames: errorToastClassName(),
        })
      }
    },
  })

  const handleOnArchiveMember = () => {
    confirmPopup.show({
      title: word("box.member.archive.confirmPopup.title"),
      message: word("box.member.archive.confirmPopup.description"),
      accept: async () => {
        try {
          await archiveMember()
          hideSidebar()
          toast.success(word("box.member.archive.confirmPopup.success"), {
            classNames: successToastClassName(),
          })
        } catch {
          toast.error(word("box.member.archive.confirmPopup.error"), {
            classNames: errorToastClassName(),
          })
        }
      },
      footerProps: {
        confirmText: word("box.member.archive.confirmPopup.confirm"),
      },
    })
  }

  const handleSendOfferModal = () => {
    memberSendOfferModalRef.current?.open()
  }

  return (
    <div className={cn("flex w-full flex-col gap-8 px-4 pt-8 pb-6", className)} {...props}>
      {/* LAST VISIT & NO SHOW */}
      <div className="mt-2 flex">
        {/* LAST VISIT*/}
        <div className="flex flex-1 flex-col">
          <Text fontStyle="TM" className="text-textDefault-body">
            {word("box.member.table.lastVisit")}
          </Text>
          <div className="mt-2 flex items-center gap-2">
            <HuCamembert number={lastvisited.number} />
            <div className="flex flex-col">
              {memberDetailed.lastVisited ? (
                <>
                  <Text fontStyle="BM" className="text-textDefault-body">
                    {word("box.member.table.daysAgo", {
                      count: lastvisited.daysSinceLastVisited === 0 ? 1 : lastvisited.daysSinceLastVisited,
                    })}
                  </Text>

                  <Text fontStyle="BS" className="text-textDefault-caption">
                    {`(${formatDateWithHyphen(dayjs(memberDetailed.lastVisited).format("L"))})`}
                  </Text>
                </>
              ) : (
                <Text fontStyle="BM" className="text-textDefault-caption">
                  {word("box.member.table.noVisitYet")}
                </Text>
              )}
            </div>
          </div>
        </div>
        {/* NO SHOW */}
        <div className="flex flex-1 flex-col">
          <Text fontStyle="TM" className="text-textDefault-body">
            {word("box.member.table.noShow", {
              count: memberDetailed.noShowPastMonth === 0 ? 1 : memberDetailed.noShowPastMonth,
            })}
          </Text>
          <div className="mt-2 flex items-center gap-2">
            <HuCamembert number={memberDetailed.convertNoShow()} />
            <div className="flex flex-col">
              <Text fontStyle="BM" className="text-textDefault-body">
                {memberDetailed.noShowPastMonth}{" "}
                {word("box.member.table.noShow", {
                  count: memberDetailed.noShowPastMonth === 0 ? 1 : memberDetailed.noShowPastMonth,
                }).toLowerCase()}
              </Text>
              <Text fontStyle="BS" className="text-textDefault-caption">
                {word("box.member.table.dayToDay.pastMonth")}
              </Text>
            </div>
          </div>
        </div>
      </div>
      {/* NEXT BOOKING */}
      <div className="flex flex-col">
        <Text fontStyle="TM" className="text-textDefault-body">
          {word("box.member.table.dayToDay.nextBooking_plural", { count: memberDetailed.nextBookings.length })}
        </Text>
        <div className="mt-2">
          {memberDetailed.nextBookings.length > 0 ? (
            <Swiper
              spaceBetween={10}
              slidesPerView={3}
              freeMode={true}
              grabCursor={true}
              pagination={{
                clickable: true,
              }}
              modules={[FreeMode, Pagination, Navigation]}
            >
              {memberDetailed.nextBookings.map((booking) => (
                <SwiperSlide key={booking.slotId}>
                  <HuNextBookingCard nextBooking={booking} />
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <HuNextBookingCard />
          )}
        </div>
      </div>
      {/* INTERNAL NOTE*/}
      <div className="flex flex-col gap-2">
        <Text fontStyle="TM" className="text-textDefault-body">
          {word("box.member.table.dayToDay.internalNote.label")}
        </Text>
        <div className="mt-2 mb-2 flex flex-row gap-2">
          <WarningIcon color="currentColor" width={24} height={24} className="shrink-0 text-textDefault-body" />
          <Text fontStyle="BS" className="text-textDefault-caption">
            {word("box.member.table.dayToDay.internalNote.warning")}
          </Text>
        </div>

        <form.AppField name="note">
          {(field) => (
            <field.Field>
              <field.Content>
                <field.Textarea
                  disabled={!canEditMember}
                  placeholder={word("member.form.label.note")}
                  className="field-sizing-fixed"
                  rows={5}
                />
                <field.Message />
              </field.Content>
            </field.Field>
          )}
        </form.AppField>
      </div>
      {/* ACTIVE OFFERS */}
      <div className="flex flex-col gap-4">
        <Text fontStyle="TM" className="text-textDefault-body">
          {word("box.member.table.activeOffer.label", {
            count: memberDetailed.activeOffersTitles?.length === 0 ? 1 : 2,
          })}
        </Text>
        <Text fontStyle="BS" className="text-textDefault-caption">
          {word("box.member.table.activeOffer.subtitle")}
        </Text>

        {memberDetailed.activeOffersTitles && memberDetailed.activeOffersTitles.length > 0 ? (
          <div className="mt-2 flex flex-wrap rounded-3xl border border-borderPrimary p-6">
            {memberDetailed.activeOffersTitles.map((offer, index) => (
              <Text key={index} fontStyle="LM" className="m-1 rounded-3xl bg-surface-inverted p-4 text-black">
                {offer}
              </Text>
            ))}
          </div>
        ) : (
          <>
            <Text fontStyle="BM">{word("box.member.table.activeOffer.empty")}</Text>
            <div className="flex flex-row items-center justify-between gap-4">
              {/* Archive member */}
              {canArchiveMember && (
                <div
                  onClick={handleOnArchiveMember}
                  className="transition-duration-100 mb-0 flex flex-1 cursor-pointer flex-col items-center justify-center gap-2 rounded-3xl border border-borderDefault bg-neutral-50 px-4 py-2 text-center transition-all hover:border-borderPrimary"
                >
                  <BoxTerminatedIcon color={""} />
                  <Text fontStyle="TS" className="text-textDefault-body">
                    {word("box.member.table.dayToDay.archiveMember")}
                  </Text>
                </div>
              )}
              {/* Send an offer */}
              {canSendOffer && (
                <div
                  onClick={handleSendOfferModal}
                  className="transition-duration-100 mb-0 flex flex-1 cursor-pointer flex-col items-center justify-center gap-2 rounded-3xl border border-borderDefault bg-neutral-50 px-4 py-2 text-center transition-all hover:border-borderPrimary"
                >
                  <img src={boxOfferPlanIcon} />
                  <Text fontStyle="TS" className="text-textDefault-body">
                    {word("box.member.table.dayToDay.sendAnOffer")}
                  </Text>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {/* PERMISSIONS - Head-coach & Coach & Financial permissions denied */}
      {canEditMember && (
        <form.Subscribe selector={(s) => [s.isSubmitting, s.isDirty, s.canSubmit]}>
          {([isSubmitting, isDirty, canSubmit]) => (
            <div className="flex w-full flex-row items-center justify-center gap-2">
              <Button
                variant="outline"
                size="lg"
                onClick={() => {
                  hideSidebar()
                }}
                className="grow"
              >
                {word("global.cancel")}
              </Button>
              <Button
                variant="primary"
                size="lg"
                onClick={() => void form.handleSubmit()}
                disabled={!isDirty || !canSubmit || isSubmitting}
                className="grow"
                isLoading={isSubmitting}
              >
                {word("global.save")}
              </Button>
            </div>
          )}
        </form.Subscribe>
      )}

      <MemberSendOfferModal userId={memberDetailed.userId} memberId={memberDetailed.id} ref={memberSendOfferModalRef} />
    </div>
  )
}

type MemberTabDayToDaySkeletonProps = {
  className?: string
} & React.ComponentProps<"div">
export function MemberTabDayToDaySkeleton({ className, ...props }: MemberTabDayToDaySkeletonProps) {
  return (
    <div className={cn("flex w-full flex-col gap-8", className)} {...props}>
      {/* LAST VISIT & NO SHOW */}
      <div className="mt-2 flex">
        {/* LAST VISIT*/}
        <div className="flex flex-1 flex-col">
          <Text fontStyle="TM" className="text-textDefault-body">
            {word("box.member.table.lastVisit")}
          </Text>
          <div className="mt-2 flex items-center gap-2">
            <Skeleton className="h-12 w-24 rounded-lg" />
            <div className="flex flex-col gap-1">
              <Skeleton className="w-28 rounded-lg" />
              <Skeleton className="w-24 rounded-lg" />
            </div>
          </div>
        </div>
        {/* NO SHOW */}
        <div className="flex flex-1 flex-col">
          <Text fontStyle="TM" className="text-textDefault-body">
            {word("box.member.table.noShow", { count: 1 })}
          </Text>
          <div className="mt-2 flex items-center gap-2">
            <Skeleton className="h-12 w-24 rounded-lg" />

            <div className="flex flex-col gap-1">
              <Skeleton className="w-28 rounded-lg" />
              <Skeleton className="w-24 rounded-lg" />
            </div>
          </div>
        </div>
      </div>
      {/* NEXT BOOKING */}
      <div className="flex flex-col">
        <Text fontStyle="TM" className="text-textDefault-body">
          {word("box.member.table.dayToDay.nextBooking_plural", { count: 2 })}
        </Text>
        <div className="flex-rows mt-2 flex gap-2">
          <Skeleton className="h-18 w-40 rounded-lg" />
          <Skeleton className="h-18 w-40 rounded-lg" />
          <Skeleton className="h-18 w-40 rounded-lg" />
        </div>
      </div>
      {/* INTERNAL NOTE*/}
      <div className="flex flex-col">
        <Text fontStyle="TM" className="text-textDefault-body">
          {word("box.member.table.dayToDay.internalNote.label")}
        </Text>
        <div className="mt-2 mb-2 flex flex-row gap-2">
          <WarningIcon color="var(--color-neutral-900)" width={20} height={20} />
          <Text fontStyle="BS" className="text-textDefault-caption">
            {word("box.member.table.dayToDay.internalNote.warning")}
          </Text>
        </div>
        <Skeleton className="h-28 rounded-lg" />
      </div>
      {/* ACTIVE OFFERS */}
      <div className="flex flex-col">
        <Text fontStyle="TM" className="text-textDefault-body">
          {word("box.member.table.activeOffer.label", { count: 1 })}
        </Text>
        <Text fontStyle="BS" className="mt-2 text-textDefault-caption">
          {word("box.member.table.activeOffer.subtitle")}
        </Text>
        <Skeleton className="mt-2 h-28 rounded-lg" />
      </div>
      {/* BUTTON */}
      <div className="mb-6 flex flex-row items-center justify-center gap-2">
        <Skeleton className="h-14 rounded-full" />
        <Skeleton className="h-14 rounded-full" />
      </div>
    </div>
  )
}
